import * as React from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { cn, formatDateEuropean, parseDateEuropean } from "@/lib/utils"
import { Calendar } from "@/components/ui/shadcn/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/popover"

interface DatePickerProps {
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  contentSide?: "top" | "right" | "bottom" | "left"
}

function normalizeDateValue(value?: string): string {
  const normalized = value?.trim() ?? ""
  if (!normalized) {
    return ""
  }

  const isoDate = /^(\d{4}-\d{2}-\d{2})/.exec(normalized)
  if (isoDate) {
    return isoDate[1]
  }

  const parsed = new Date(normalized)
  if (Number.isNaN(parsed.getTime())) {
    return normalized
  }

  return format(parsed, "yyyy-MM-dd")
}

/**
 * A date field: European `dd.MM.yyyy` on screen, ISO `yyyy-MM-dd` on the wire.
 *
 * The two formats are deliberately separate. Every caller and every endpoint
 * exchanges ISO, so `value`/`onValueChange` never change shape; only what the
 * reader sees and types is localised.
 */
export function DatePicker({
  value,
  onValueChange,
  placeholder = "Pick a date",
  className,
  disabled = false,
  contentSide = "bottom",
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const normalizedValue = normalizeDateValue(value)
  const [draftValue, setDraftValue] = React.useState(() =>
    normalizedValue ? formatDateEuropean(normalizedValue) : ""
  )

  const date = normalizedValue ? new Date(normalizedValue) : undefined

  React.useEffect(() => {
    const normalized = normalizeDateValue(value)
    setDraftValue(normalized ? formatDateEuropean(normalized) : "")
  }, [value])

  const applyDraftValue = (nextValue: string) => {
    const normalized = nextValue.trim()
    if (normalized === "") {
      onValueChange?.("")
      return
    }

    // European first: 03.04.2026 is 3 April here, but `new Date()` would read
    // that same string as 4 March.
    const parsed = parseDateEuropean(normalized) ?? new Date(normalized)
    if (Number.isNaN(parsed.getTime())) {
      // Leave the draft alone so the user can correct their own typing rather
      // than having it silently reverted.
      return
    }

    onValueChange?.(format(parsed, "yyyy-MM-dd"))
    setDraftValue(formatDateEuropean(parsed))
  }

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      onValueChange?.(format(selectedDate, 'yyyy-MM-dd'))
      setDraftValue(formatDateEuropean(selectedDate))
    } else {
      onValueChange?.('')
      setDraftValue("")
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "border-input bg-background ring-offset-background focus-within:ring-ring/50 focus-within:ring-[3px] flex h-10 w-full items-center rounded-md border px-3 py-1 text-base md:h-9 md:text-sm",
            disabled && "pointer-events-none opacity-50",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={draftValue}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(event) => setDraftValue(event.target.value)}
            onBlur={() => applyDraftValue(draftValue)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                applyDraftValue(draftValue)
              }
            }}
            className={cn(
              // 16px under md, otherwise iOS Safari zooms the page on focus.
              "w-full border-0 bg-transparent p-0 text-base outline-none md:text-sm",
              !date && "text-muted-foreground"
            )}
          />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side={contentSide} sideOffset={8} collisionPadding={16}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleDateSelect}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
} 
